/**
 * Everywhere checkout validation library (handoff §3.3).
 * Identical logic everywhere a field appears; error copy is verbatim and
 * canonical — do not paraphrase.
 */

export const BLOCKED_STATE_CODES = [
  'NY',
  'NJ',
  'RI',
  'AZ',
  'OK',
  'MA',
  'PA',
] as const;

export type BlockedStateCode = (typeof BLOCKED_STATE_CODES)[number];

export const BLOCKED_STATE_NAMES: Record<BlockedStateCode, string> = {
  NY: 'New York',
  NJ: 'New Jersey',
  RI: 'Rhode Island',
  AZ: 'Arizona',
  OK: 'Oklahoma',
  MA: 'Massachusetts',
  PA: 'Pennsylvania',
};

const oxfordList = (items: readonly string[]): string =>
  `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;

/** "NY, NJ, RI, AZ, OK, MA, and PA" — the canonical inline list for copy. */
export const BLOCKED_STATE_LIST = oxfordList(BLOCKED_STATE_CODES);

/** Same list, spelled out, for legal and footer disclaimers. */
export const BLOCKED_STATE_NAME_LIST = oxfordList(
  BLOCKED_STATE_CODES.map((code) => BLOCKED_STATE_NAMES[code]),
);

export const blockedStateError = (stateCode: BlockedStateCode): string =>
  `Testing isn't available in ${BLOCKED_STATE_NAMES[stateCode]} yet.`;

/* ---------------------------------- phone --------------------------------- */

export const phoneDigits = (value: string): string =>
  value.replace(/\D/g, '').replace(/^1/, '').slice(0, 10);

/** Live formats to (555) 555-0100 as the user types. */
export function formatPhoneInput(value: string): string {
  const d = phoneDigits(value);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const isValidPhone = (value: string): boolean =>
  phoneDigits(value).length === 10;

/* ----------------------------------- DOB ---------------------------------- */

export const DOB_INVALID_ERROR =
  "That date doesn't look right. Check it and try again.";
export const DOB_MINOR_ERROR = 'Testing is for adults 18 and older.';

/** Masks digits to MM / DD / YYYY (auto-inserts " / " after 2 and 4 digits). */
export function formatDobInput(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)} / ${d.slice(2)}`;
  return `${d.slice(0, 2)} / ${d.slice(2, 4)} / ${d.slice(4)}`;
}

export type DobStatus = 'partial' | 'invalid' | 'minor' | 'valid';

export function dobStatus(value: string, now: Date = new Date()): DobStatus {
  const d = value.replace(/\D/g, '');
  if (d.length < 8) return 'partial';

  const month = Number(d.slice(0, 2));
  const day = Number(d.slice(2, 4));
  const year = Number(d.slice(4, 8));

  const date = new Date(year, month - 1, day);
  const realDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  const age = ageInYears(date, now);

  if (!realDate || year < 1900 || year > now.getFullYear() || age > 120) {
    return 'invalid';
  }

  return age < 18 ? 'minor' : 'valid';
}

export function ageInYears(dob: Date, now: Date = new Date()): number {
  let age = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

/** MM / DD / YYYY input → YYYY-MM-DD (for consumer persistence). */
export function dobToIso(value: string): string {
  const d = value.replace(/\D/g, '');
  return `${d.slice(4, 8)}-${d.slice(0, 2)}-${d.slice(2, 4)}`;
}

/** YYYY-MM-DD profile value → MM / DD / YYYY checkout display value. */
export function dobFromIso(value: string | null | undefined): string {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]} / ${match[3]} / ${match[1]}` : '';
}

/* ----------------------------------- ZIP ---------------------------------- */

export const zipDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 5);

export const isValidZipInput = (value: string): boolean =>
  zipDigits(value).length === 5;

/**
 * Launch-grade blocked-state resolution. USPS assigns ZIPs to states in
 * complete, documented ranges — including the 0-prefix oddballs (00501/00544
 * Holtsville NY, 06390 Fishers Island NY, 05501/05544 Andover MA) that the
 * 3-digit-prefix trick misses.
 */
export function blockedStateForZip(zip: string): BlockedStateCode | null {
  const z = zipDigits(zip);
  if (z.length !== 5) return null;

  const n = Number(z);
  const prefix = Number(z.slice(0, 3));

  // New York: 100xx–149xx, plus 00501/00544 (Holtsville IRS) and 06390
  // (Fishers Island, the lone 0-prefix NY community).
  if (
    (prefix >= 100 && prefix <= 149) ||
    n === 501 ||
    n === 544 ||
    n === 6390
  ) {
    return 'NY';
  }
  // Massachusetts: 010xx–027xx, plus 05501/05544 (Andover IRS, which sit in
  // Vermont's 05x block).
  if ((prefix >= 10 && prefix <= 27) || n === 5501 || n === 5544) return 'MA';
  // Rhode Island: 028xx–029xx.
  if (prefix === 28 || prefix === 29) return 'RI';
  // New Jersey: 070xx–089xx.
  if (prefix >= 70 && prefix <= 89) return 'NJ';
  // Pennsylvania: 150xx–196xx (197xx+ is Delaware).
  if (prefix >= 150 && prefix <= 196) return 'PA';
  // Oklahoma: 730xx–749xx.
  if (prefix >= 730 && prefix <= 749) return 'OK';
  // Arizona: 850xx–865xx.
  if (prefix >= 850 && prefix <= 865) return 'AZ';

  return null;
}

/** Normalizes an address state code to the blocked state it names, or null. */
export function blockedResidenceState(
  stateCode: string,
): BlockedStateCode | null {
  const code = stateCode.trim().toUpperCase();
  return (BLOCKED_STATE_CODES as readonly string[]).includes(code)
    ? (code as BlockedStateCode)
    : null;
}

export const isBlockedResidenceState = (stateCode: string): boolean =>
  blockedResidenceState(stateCode) !== null;

/* ------------------------------ at-home kits ------------------------------ */

/** At-home kits ship everywhere but New York. */
export const KIT_BLOCKED_STATE_CODES: readonly BlockedStateCode[] = ['NY'];

export const kitBlockedStateError = (stateCode: BlockedStateCode): string =>
  `Kits aren't available in ${BLOCKED_STATE_NAMES[stateCode]} yet.`;

/** The kit stopper for a ZIP: NY only; every other lab-blocked state ships. */
export function kitBlockedStateForZip(zip: string): BlockedStateCode | null {
  const code = blockedStateForZip(zip);
  return code && (KIT_BLOCKED_STATE_CODES as readonly string[]).includes(code)
    ? code
    : null;
}

/** Normalizes an address state code to the kit-blocked state it names, or null. */
export function kitBlockedResidenceState(
  stateCode: string,
): BlockedStateCode | null {
  const code = blockedResidenceState(stateCode);
  return code && (KIT_BLOCKED_STATE_CODES as readonly string[]).includes(code)
    ? code
    : null;
}

/* ---------------------------------- email --------------------------------- */

export const isValidEmailShape = (value: string): boolean =>
  /^\S+@\S+\.\S+$/.test(value.trim());

/* ----------------------------------- OTP ---------------------------------- */

export const otpDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, 6);
